import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import {
  ApiConfig,
  AppRuntimeConfig,
  KeycloakConfig,
} from '../../../environments/environment.model';

@Injectable({ providedIn: 'root' })
export class ConfigService {
  private loaded = false;
  private loadPromise: Promise<void> | null = null;

  get api(): ApiConfig {
    return environment.api;
  }

  get keycloak(): KeycloakConfig {
    return environment.keycloak;
  }

  get production(): boolean {
    return environment.production;
  }

  get enableApiLogs(): boolean {
    return Boolean(environment.enableApiLogs);
  }

  resolveKeycloakTokenUrl(): string {
    const url = (environment.keycloak.url || '').replace(/\/$/, '');
    const ssoUrl = environment.keycloak.ssoUrl || '';

    if (!url) {
      return ssoUrl;
    }

    return `${url}${ssoUrl.startsWith('/') ? ssoUrl : `/${ssoUrl}`}`;
  }

  /**
   * Charge /assets/config.json une seule fois (fetch natif, hors intercepteurs HTTP)
   * et l’applique sur `environment`.
   */
  loadAppConfig(): Promise<void> {
    if (this.loaded) {
      return Promise.resolve();
    }
    if (this.loadPromise) {
      return this.loadPromise;
    }

    const configUrl = new URL(
      `assets/config.json?t=${Date.now()}`,
      document.baseURI,
    ).toString();

    this.loadPromise = fetch(configUrl, { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json() as Promise<AppRuntimeConfig>;
      })
      .then((runtime) => this.applyRuntimeConfig(runtime))
      .catch((error) => {
        console.warn(
          '[Config] Impossible de charger assets/config.json, fallback environment',
          error,
        );
      })
      .then(() => {
        this.loaded = true;
        if (this.enableApiLogs) {
          console.log('[Config] runtime', {
            production: environment.production,
            urlBase: environment.api.urlBase,
            uriSuffixe: environment.api.uriSuffixe,
            keycloak: environment.keycloak.url,
            realm: environment.keycloak.realm,
          });
        }
      });

    return this.loadPromise;
  }

  private applyRuntimeConfig(
    runtime: AppRuntimeConfig | null | undefined,
  ): void {
    if (!runtime) {
      return;
    }

    if (typeof runtime.production === 'boolean') {
      environment.production = runtime.production;
    }
    if (typeof runtime.enableApiLogs === 'boolean') {
      environment.enableApiLogs = runtime.enableApiLogs;
    }
    if (runtime.keycloak) {
      Object.assign(environment.keycloak, runtime.keycloak);
    }
    if (runtime.api) {
      if (runtime.api.urlBase != null) {
        environment.api.urlBase = runtime.api.urlBase.replace(/\/$/, '');
      }
      if (runtime.api.uriSuffixe != null) {
        environment.api.uriSuffixe = runtime.api.uriSuffixe;
      }
      if (runtime.api.wsBaseUrl != null) {
        environment.api.wsBaseUrl = runtime.api.wsBaseUrl;
      }
    }
  }
}
